import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, CreditCard, Gift, LifeBuoy, TrendingUp, Calendar, ArrowRight, Gauge, Clock } from "lucide-react";
import { api, inr, fmtDate } from "@/lib/api";
import { Skeleton, StatusBadge, Progress, EmptyState } from "@/components/common";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { HealthScore } from "@/components/HealthScore";
import { useT } from "@/context/i18n";

const scoreColor = { Excellent: "#14B8A6", Good: "#14B8A6", Average: "#F59E0B", Poor: "#EF4444" };

function ScoreRing({ score, band }) {
  const c = scoreColor[band] || "#14B8A6";
  const r = 52, circ = 2 * Math.PI * r, off = circ - (score / 100) * circ;
  return (
    <div className="relative grid place-items-center">
      <svg width="140" height="140" className="-rotate-90">
        <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={c} strokeWidth="10" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute text-center"><p className="font-display text-3xl font-bold text-white">{score}</p><p className="text-xs font-semibold" style={{ color: c }}>{band}</p></div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [pendingReward, setPendingReward] = useState(0);
  const t = useT();
  const nvg = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/wallet").then((r) => setPendingReward(r.data.pending_balance || 0)).catch(() => {}); }, []);

  if (!d) return <div className="space-y-6"><Skeleton className="h-40 w-full" /><div className="grid gap-4 sm:grid-cols-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-64 w-full" /></div>;

  const quick = [{ to: "/loans", label: "Pay EMI", icon: CreditCard }, { to: "/loans", label: "View Loans", icon: Wallet }, { to: "/payments", label: "History", icon: TrendingUp }, { to: "/rewards", label: "Rewards", icon: Gift }, { to: "/support", label: "Support", icon: LifeBuoy }];

  return (
    <div className="space-y-6">
      {/* Health hero */}
      <div className="overflow-hidden rounded-2xl navy-grain p-6 text-white">
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
          <div>
            <p className="text-sm text-slate-300">{t("Good to see you,")}</p>
            <h1 className="font-display text-2xl font-bold sm:text-3xl">{d.customer_name}</h1>
            <p className="mt-2 max-w-md text-sm text-slate-400">{t("Here's your EMI Health Score — a simple, honest view of your repayment health.")}</p>
            <Link to="/credit-score" className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/15"><Gauge size={14} /> {t("View credit score")}</Link>
          </div>
          <ScoreRing score={d.health_score} band={d.health_band} />
        </div>
      </div>

      {/* Reward on the way (pending lender confirmation) */}
      {pendingReward > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="reward-pending-banner">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-600"><Clock size={18} /></span>
          <div>
            <p className="font-display font-bold text-[#0B132B]">₹{pendingReward.toLocaleString("en-IN")} {t("Reward on the way")}</p>
            <p className="text-xs text-slate-500">{t("Your reward will be added to your ZRo Pe Wallet after lender confirmation.")}</p>
          </div>
          <Link to="/wallet" className="ml-auto shrink-0 text-xs font-semibold text-[#14B8A6]">{t("View wallet")} →</Link>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["Active Loans", d.active_loans], ["Monthly EMI", inr(d.monthly_emi)], ["Outstanding", inr(d.outstanding)], ["Next Due", fmtDate(d.next_due_date)]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{t(l)}</p>
            <p className="mt-1 font-display text-xl font-bold tracking-tight text-[#0B132B]">{v}</p>
          </div>
        ))}
      </div>

      {/* EMI Health Score (interactive) */}
      <HealthScore data={d} />

      {/* Loan Overview */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div><h2 className="font-display text-lg font-bold text-[#0B132B]">{t("Loan Overview")}</h2><p className="text-sm text-slate-500">{d.active_loans} active loans • {inr(d.outstanding)} outstanding</p></div>
        <div className="mt-4 flex flex-col items-center gap-6 sm:flex-row">
          <div className="h-44 w-44 shrink-0">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
              <PieChart>
                <Pie data={d.loans.map((l) => ({ name: l.loan_type, value: l.outstanding }))} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                  {d.loans.map((l, i) => (<Cell key={i} fill={["#14B8A6", "#0B132B", "#F59E0B", "#64748b"][i % 4]} />))}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full flex-1 space-y-2">
            {d.loans.length === 0 ? <p className="text-sm text-slate-400">{t("No active loans to show.")}</p> : d.loans.map((l, i) => (
              <div key={l.id} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: ["#14B8A6", "#0B132B", "#F59E0B", "#64748b"][i % 4] }} /> <span className="font-medium text-[#0B132B]">{l.lender}</span> <span className="text-slate-400">{l.loan_type}</span></span>
                <span className="font-semibold text-[#0B132B]">{inr(l.outstanding)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {quick.map((q) => (
          <Link key={q.label} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, "-")}`}
            className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white p-4 text-center shadow-sm transition hover:border-[#14B8A6]/50 hover:shadow-md">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-emerald-50 text-[#14B8A6]"><q.icon size={20} /></span>
            <span className="text-xs font-semibold text-[#0B132B]">{t(q.label)}</span>
          </Link>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Loans */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-[#0B132B]">{t("Active Loans")}</h2><Link to="/loans" className="text-sm font-semibold text-[#14B8A6]">{t("View all")}</Link></div>
          {d.loans.length === 0 ? <EmptyState icon={Wallet} title={t("No active loans")} desc={t("Once your loans are linked, they'll appear here.")} /> : d.loans.map((l) => {
            const prog = Math.round(((l.tenure_months - l.remaining_emi) / l.tenure_months) * 100);
            return (
              <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div><p className="font-display font-bold text-[#0B132B]">{l.lender}</p><p className="text-xs text-slate-500">{l.loan_type}</p></div>
                  <StatusBadge status={l.status} />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                  <div><p className="text-xs text-slate-400">{t("EMI")}</p><p className="font-semibold text-[#0B132B]">{inr(l.emi_amount)}</p></div>
                  <div><p className="text-xs text-slate-400">{t("Outstanding")}</p><p className="font-semibold text-[#0B132B]">{inr(l.outstanding)}</p></div>
                  <div><p className="text-xs text-slate-400">{t("Next Due")}</p><p className="font-semibold text-[#0B132B]">{fmtDate(l.next_due_date)}</p></div>
                </div>
                <div className="mt-4"><div className="mb-1 flex justify-between text-xs text-slate-500"><span>{prog}% paid</span><span>{l.remaining_emi} EMIs left</span></div><Progress value={prog} /></div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => nvg("/payment", { state: { loan: l } })} data-testid={`pay-${l.id}`} className="flex-1 rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]">{t("Pay EMI")}</button>
                  <Link to={`/loans/${l.id}`} className="flex items-center gap-1 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50">{t("Details")} <ArrowRight size={14} /></Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2"><Calendar size={16} className="text-[#14B8A6]" /><h3 className="font-display font-bold text-[#0B132B]">{t("Upcoming EMIs")}</h3></div>
            <div className="mt-3 space-y-2">
              {d.loans.filter((l) => l.next_due_date).sort((a, b) => a.next_due_date.localeCompare(b.next_due_date)).map((l) => (
                <div key={l.id} className="flex items-center justify-between rounded-md bg-slate-50 p-3 text-sm"><div><p className="font-medium text-[#0B132B]">{l.lender}</p><p className="text-xs text-slate-400">{fmtDate(l.next_due_date)}</p></div><span className="font-semibold text-[#0B132B]">{inr(l.emi_amount)}</span></div>
              ))}
              {d.loans.filter((l) => l.next_due_date).length === 0 && <p className="py-4 text-center text-sm text-slate-400">{t("No upcoming EMIs 🎉")}</p>}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between"><h3 className="font-display font-bold text-[#0B132B]">{t("Rewards")}</h3><Link to="/rewards" className="text-xs font-semibold text-[#14B8A6]">{t("Redeem")}</Link></div>
            <p className="mt-2 font-display text-3xl font-bold text-[#0B132B]">{d.reward_points.toLocaleString("en-IN")} <span className="text-sm font-medium text-slate-400">ZRo Pe Coins</span></p>
            <p className="text-xs text-slate-400">+ {inr(d.cashback)} cashback available</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="font-display font-bold text-[#0B132B]">{t("Recent Payments")}</h3>
            <div className="mt-3 space-y-2">
              {d.recent_payments.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">{t("No payments yet")}</p> : d.recent_payments.slice(0, 4).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm"><div><p className="font-medium text-[#0B132B]">{p.lender}</p><p className="text-xs text-slate-400">{fmtDate(p.created_at)}</p></div><div className="text-right"><p className="font-semibold text-[#0B132B]">{inr(p.amount)}</p><StatusBadge status={p.status} /></div></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
